import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { admissionsFeed, myApplications, programmes as allProgrammes, scholarships as allScholarships, universityProfiles } from '../data/admissions';
import type { Programme, Scholarship, UniversityProfile } from '../types/admissions';
import { defaultFilters, filterProgrammes, filterScholarships, filterUniversities, matchFor, sortUniversities, TUITION_CEILING, type DiscoverFilters, type SortMode } from '../utils/discover';
import { useDesignVariant } from '../hooks/useDesignVariant';

/**
 * Discover's two compositions, expressed in the terms this context already
 * uses. The schedule itself belongs to the shared design variant controller —
 * this context does not own a timer.
 */
export type DiscoverVariant = 'A' | 'B';
export type VariantMode = 'auto' | DiscoverVariant;
import { useAccount } from './AccountContext';

/**
 * One state layer beneath both Discover compositions.
 *
 * Search, filters, sort, view, saved items, comparison and the open section all
 * live here. When the presented composition changes, this provider is not
 * remounted — so nothing the student has done is lost, and neither design owns
 * a private copy of the truth.
 */

export type DiscoverSection = 'Universities' | 'Programmes' | 'Scholarships' | 'Applications' | 'Feed' | 'Saved' | 'Compare';
export type SaveKind = 'university' | 'programme' | 'scholarship';
export type ViewMode = 'Grid' | 'List' | 'Map';
export const COMPARE_LIMIT = 3;
export interface FilterChip {
  key: keyof DiscoverFilters;
  label: string;
}
interface DiscoverContextValue {
  /* presentation */
  variant: DiscoverVariant;
  variantMode: VariantMode;
  setVariantMode: (mode: VariantMode) => void;

  /* navigation */
  section: DiscoverSection;
  setSection: (section: DiscoverSection) => void;

  /* search + refinement */
  query: string;
  setQuery: (query: string) => void;
  filters: DiscoverFilters;
  setFilter: <K extends keyof DiscoverFilters>(key: K, value: DiscoverFilters[K]) => void;
  clearFilter: (key: keyof DiscoverFilters) => void;
  clearFilters: () => void;
  activeChips: FilterChip[];
  sort: SortMode;
  setSort: (sort: SortMode) => void;
  view: ViewMode;
  setView: (view: ViewMode) => void;

  /* options, derived from the records themselves */
  countries: string[];
  locations: string[];
  fields: string[];

  /* results */
  universities: UniversityProfile[];
  programmes: Programme[];
  scholarships: Scholarship[];
  allUniversities: UniversityProfile[];
  allProgrammes: Programme[];
  applications: typeof myApplications;
  feed: typeof admissionsFeed;

  /* personalisation */
  studentField?: string;
  studentLevel?: string;
  matchFor: (university: UniversityProfile) => {
    score: number;
    reason: string;
  };

  /* saving */
  isSaved: (kind: SaveKind, id: string) => boolean;
  toggleSave: (kind: SaveKind, id: string) => void;
  savedUniversities: UniversityProfile[];
  savedProgrammes: Programme[];
  savedScholarships: Scholarship[];
  savedCount: number;

  /* comparison */
  compareIds: string[];
  compared: UniversityProfile[];
  isCompared: (id: string) => boolean;
  toggleCompare: (id: string) => void;
  removeCompare: (id: string) => void;
  clearCompare: () => void;
  compareOpen: boolean;
  setCompareOpen: (open: boolean) => void;
  compareFull: boolean;
}
const DiscoverContext = createContext<DiscoverContextValue | null>(null);
export function DiscoverProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    account
  } = useAccount();

  // Personalisation reads only what the student's own record already says. The
  // field is matched against the fields Campuzon actually publishes, so an
  // unrecognised programme personalises nothing rather than guessing.
  const programmeTitle = account.academic.programme;
  const studentField = useMemo(() => {
    const known = Array.from(new Set(allProgrammes.map(item => item.field)));
    return known.find(item => programmeTitle.toLowerCase().includes(item.toLowerCase()));
  }, [programmeTitle]);

  // An undergraduate year ("300 Level") is a stage within a bachelor's degree,
  // not a programme level, so it is translated rather than compared directly.
  const studentLevel = /level/i.test(account.academic.level) ? "Bachelor's" : undefined;

  // One controller decides every surface's presentation; Discover just maps
  // its answer onto the A/B names this context is written in.
  const presentation = useDesignVariant('DISCOVER');
  const rotation = {
    variant: (presentation.design === 'DESIGN_2' ? 'B' : 'A') as DiscoverVariant,
    mode: (presentation.preview === 'auto' ? 'auto' : presentation.preview === 'DESIGN_1' ? 'A' : 'B') as VariantMode,
    setMode: (mode: VariantMode) => presentation.setPreview(mode === 'auto' ? 'auto' : mode === 'A' ? 'DESIGN_1' : 'DESIGN_2'),
    intervalMs: presentation.config.rotationIntervalMs
  };
  const [section, setSection] = useState<DiscoverSection>('Universities');
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<DiscoverFilters>(defaultFilters);
  const [sort, setSort] = useState<SortMode>('Recommended');
  const [view, setView] = useState<ViewMode>('Grid');
  const [saved, setSaved] = useState<Record<SaveKind, string[]>>({
    university: [],
    programme: [],
    scholarship: []
  });
  const [compareIds, setCompareIds] = useState<string[]>([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const setFilter = useCallback(<K extends keyof DiscoverFilters,>(key: K, value: DiscoverFilters[K]) => {
    setFilters(current => ({
      ...current,
      [key]: value
    }));
  }, []);
  const clearFilter = useCallback((key: keyof DiscoverFilters) => {
    setFilters(current => ({
      ...current,
      [key]: defaultFilters[key]
    }));
  }, []);
  const clearFilters = useCallback(() => setFilters(defaultFilters), []);
  const toggleSave = useCallback((kind: SaveKind, id: string) => {
    setSaved(current => {
      const list = current[kind];
      return {
        ...current,
        [kind]: list.includes(id) ? list.filter(item => item !== id) : [...list, id]
      };
    });
  }, []);
  const toggleCompare = useCallback((id: string) => {
    setCompareIds(current => {
      if (current.includes(id)) return current.filter(item => item !== id);
      // The cap is a product decision, not a rendering one: three institutions
      // is the most that can be read side by side without a horizontal scroll.
      if (current.length >= COMPARE_LIMIT) return current;
      return [...current, id];
    });
  }, []);
  const context = useMemo(() => ({
    field: studentField,
    level: studentLevel
  }), [studentField, studentLevel]);
  const universities = useMemo(() => sortUniversities(filterUniversities(universityProfiles, allProgrammes, filters, query), allProgrammes, sort, context), [filters, query, sort, context]);
  const programmes = useMemo(() => filterProgrammes(allProgrammes, universityProfiles, filters, query), [filters, query]);
  const scholarships = useMemo(() => filterScholarships(allScholarships, filters, query), [filters, query]);
  const activeChips = useMemo<FilterChip[]>(() => {
    const chips: FilterChip[] = [];
    if (filters.country !== 'All') chips.push({
      key: 'country',
      label: filters.country
    });
    if (filters.institutionType !== 'All') chips.push({
      key: 'institutionType',
      label: filters.institutionType
    });
    if (filters.location !== 'All') chips.push({
      key: 'location',
      label: filters.location
    });
    if (filters.field !== 'All') chips.push({
      key: 'field',
      label: filters.field
    });
    if (filters.level !== 'All') chips.push({
      key: 'level',
      label: filters.level
    });
    if (filters.tuitionMax < TUITION_CEILING) chips.push({
      key: 'tuitionMax',
      label: `Under ₦${filters.tuitionMax.toLocaleString()}`
    });
    if (filters.scholarshipsOnly) chips.push({
      key: 'scholarshipsOnly',
      label: 'Scholarships'
    });
    if (filters.internationalOnly) chips.push({
      key: 'internationalOnly',
      label: 'International intake'
    });
    if (filters.deadline !== 'Anytime') chips.push({
      key: 'deadline',
      label: filters.deadline
    });
    return chips;
  }, [filters]);
  const value = useMemo<DiscoverContextValue>(() => {
    const savedUniversities = universityProfiles.filter(item => saved.university.includes(item.id));
    const savedProgrammes = allProgrammes.filter(item => saved.programme.includes(item.id));
    const savedScholarships = allScholarships.filter(item => saved.scholarship.includes(item.id));
    return {
      variant: rotation.variant,
      variantMode: rotation.mode,
      setVariantMode: rotation.setMode,
      section,
      setSection,
      query,
      setQuery,
      filters,
      setFilter,
      clearFilter,
      clearFilters,
      activeChips,
      sort,
      setSort,
      view,
      setView,
      countries: ['All', ...Array.from(new Set(universityProfiles.map(item => item.country)))],
      locations: ['All', ...Array.from(new Set(universityProfiles.map(item => item.city)))],
      fields: ['All', ...Array.from(new Set(allProgrammes.map(item => item.field)))],
      universities,
      programmes,
      scholarships,
      allUniversities: universityProfiles,
      allProgrammes,
      applications: myApplications,
      feed: admissionsFeed,
      studentField,
      studentLevel,
      matchFor: (university: UniversityProfile) => matchFor(university, allProgrammes, context),
      isSaved: (kind: SaveKind, id: string) => saved[kind].includes(id),
      toggleSave,
      savedUniversities,
      savedProgrammes,
      savedScholarships,
      savedCount: saved.university.length + saved.programme.length + saved.scholarship.length,
      compareIds,
      compared: compareIds.map(id => universityProfiles.find(item => item.id === id)).filter((item): item is UniversityProfile => Boolean(item)),
      isCompared: (id: string) => compareIds.includes(id),
      toggleCompare,
      removeCompare: (id: string) => setCompareIds(current => current.filter(item => item !== id)),
      clearCompare: () => setCompareIds([]),
      compareOpen,
      setCompareOpen,
      compareFull: compareIds.length >= COMPARE_LIMIT
    };
  }, [rotation.variant, rotation.mode, rotation.setMode, section, query, filters, setFilter, clearFilter, clearFilters, activeChips, sort, view, universities, programmes, scholarships, context, studentLevel, saved, toggleSave, compareIds, toggleCompare, compareOpen]);
  return <DiscoverContext.Provider value={value} data-id="element-5532" data-mp="34zfegxr9iycw">{children}</DiscoverContext.Provider>;
}
export function useDiscover(): DiscoverContextValue {
  const context = useContext(DiscoverContext);
  if (!context) {
    throw new Error('useDiscover must be used inside a DiscoverProvider');
  }
  return context;
}